import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, KeyRound, Lock, ArrowLeft, ArrowRight, Loader2, Check } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=new password
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const requestOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to send reset code");
      }
      toast.success("Reset code sent — check your email");
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp_code: otp }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Invalid code");
      }
      const data = await res.json();
      setResetToken(data.reset_token);
      toast.success("Code verified");
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, reset_token: resetToken, new_password: newPassword }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Reset failed");
      }
      toast.success("Password reset successfully");
      navigate("/login");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: "Email" },
    { num: 2, label: "Verify" },
    { num: 3, label: "Reset" },
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md space-y-8">
        <div className="space-y-2">
          <Link to="/" className="flex items-center gap-2 mb-6">
            <Logo className="w-9 h-9" />
          </Link>
          <h1 className="font-heading text-2xl font-bold">Reset your password</h1>
          <p className="text-sm text-muted-foreground">
            {step === 1 && "Enter your email and we'll send you a verification code."}
            {step === 2 && "Enter the 6-digit code we sent to your email."}
            {step === 3 && "Choose a new password for your account."}
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2" data-testid="step-indicator">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                step > s.num ? "bg-green-500/20 text-green-400" :
                step === s.num ? "bg-primary/20 text-primary" :
                "bg-muted text-muted-foreground"
              }`}>
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              {i < steps.length - 1 && (
                <div className={`h-px flex-1 ${step > s.num ? "bg-green-500/30" : "bg-border"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={requestOTP} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
            <Button type="submit" className="w-full gap-2" disabled={loading || !email} data-testid="send-otp-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {loading ? "Sending..." : "Send Reset Code"}
            </Button>
          </form>
        )}

        {/* Step 2: OTP */}
        {step === 2 && (
          <form onSubmit={verifyOTP} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="otp">Verification code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="pl-10 text-center tracking-[0.3em] text-lg font-mono"
                  required
                  data-testid="otp-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Check your inbox for a 6-digit code. It expires in 10 minutes.</p>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="otp-error">{error}</p>}
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => { setStep(1); setError(""); setOtp(""); }} data-testid="otp-back-btn">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Button type="submit" className="flex-1 gap-2" disabled={loading || otp.length !== 6} data-testid="verify-otp-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? "Verifying..." : "Verify Code"}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => { setError(""); requestOTP({ preventDefault: () => {} }); }}
              className="text-xs text-primary hover:underline w-full text-center"
              data-testid="resend-otp-btn"
            >
              Didn't receive the code? Resend
            </button>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <form onSubmit={resetPassword} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="new-pw">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="new-pw"
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="new-password-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pw">Confirm password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirm-pw"
                  type="password"
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" className="w-full gap-2" disabled={loading || !newPassword || !confirmPassword} data-testid="reset-password-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Remember your password?{" "}
          <Link to="/login" className="text-primary hover:underline" data-testid="back-to-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
